import { getQuoteNotifyUrl } from "@/config/api";
import { encodeCotizacionId } from "@/utils/hashids";
import { getPartnerAuthHeaders } from "@/utils/partnerAuth";
import type { QuoteData } from "@/types/quote.types";

export type NotifyCanal = "email" | "whatsapp";

export interface NotifyDestinatario {
  nombre: string;
  email: string;
  telefono?: string;
}

/**
 * Contrato notify 2026-09: el backend resuelve folio/prima/vigencia/vehículo
 * desde `idCotizacion`; el widget solo envía el hash.
 */
export interface NotifyCotizacion {
  /** Hashids del id (mismo salt que reports). */
  idCotizacion: string;
}

export interface NotifyQuoteRequest {
  destinatario: NotifyDestinatario;
  cotizacion: NotifyCotizacion;
  canales: NotifyCanal[];
}

export interface SendQuoteNotifyResponse {
  success: boolean;
  messageId?: string;
  message?: string;
  error?: string;
  code?: string;
}

/** @deprecated Alias de compatibilidad. */
export type SendQuoteEmailResponse = SendQuoteNotifyResponse;

export interface BuildNotifyQuoteParams {
  idCotizacion: number | string;
  email?: string;
  telefono?: string;
  nombre?: string;
  /** @deprecated Ignorado: el contrato notify ya no incluye prima. */
  prima?: number;
  /** @deprecated Ignorado: el contrato notify ya no incluye fechaVigencia. */
  fechaVigencia?: string;
  /** Solo se usa como fallback de nombre/email/teléfono. */
  quoteData?: QuoteData | null;
  /** @deprecated Ignorado: el contrato notify ya no incluye token. */
  token?: string;
  canales: NotifyCanal[];
}

const digitsOnly = (value: string): string => value.replace(/\D/g, "");

export const buildNotifyQuotePayload = (
  params: BuildNotifyQuoteParams,
): NotifyQuoteRequest => {
  if (!params.canales.length) {
    throw new Error("Se requiere al menos un canal");
  }

  const wantsEmail = params.canales.includes("email");
  const wantsWhatsApp = params.canales.includes("whatsapp");

  const quote = params.quoteData;
  const personal = quote?.personalData;
  const cotizacion = quote?.cotizacionData;

  const nombre =
    params.nombre?.trim() ||
    personal?.fullName?.trim() ||
    cotizacion?.nombreAsegurado?.trim() ||
    "Cliente";

  const email =
    params.email?.trim() || personal?.email?.trim() || "";
  const telefonoRaw =
    params.telefono?.trim() || personal?.whatsapp?.trim() || "";
  const telefono = telefonoRaw ? digitsOnly(telefonoRaw) : "";

  if (wantsEmail && !email) {
    throw new Error("Se requiere email del destinatario");
  }
  if (wantsWhatsApp && !telefono) {
    throw new Error("Se requiere teléfono del destinatario");
  }
  // El schema notify incluye email en destinatario también para WA.
  if (wantsWhatsApp && !email) {
    throw new Error("Se requiere email del destinatario");
  }

  const idHash = encodeCotizacionId(params.idCotizacion);

  const destinatario: NotifyDestinatario = {
    nombre,
    email,
  };
  if (telefono) {
    destinatario.telefono = telefono;
  }

  return {
    destinatario,
    cotizacion: {
      idCotizacion: idHash,
    },
    canales: [...params.canales],
  };
};

export const sendQuoteNotifyViaBackend = async (
  params: BuildNotifyQuoteParams,
): Promise<SendQuoteNotifyResponse> => {
  if (!params.idCotizacion) {
    return { success: false, error: "Se requiere idCotizacion" };
  }

  let payload: NotifyQuoteRequest;
  try {
    payload = buildNotifyQuotePayload(params);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Payload inválido",
    };
  }

  try {
    const partnerHeaders = getPartnerAuthHeaders();
    if (!partnerHeaders) {
      return {
        success: false,
        error:
          "Credenciales de partner no configuradas (VITE_KC_PARTNER_ID / VITE_KC_PARTNER_APIKEY)",
      };
    }

    const response = await fetch(getQuoteNotifyUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...partnerHeaders,
      },
      body: JSON.stringify(payload),
    });

    let data: Record<string, unknown> = {};
    try {
      data = (await response.json()) as Record<string, unknown>;
    } catch {
      data = {};
    }

    if (!response.ok) {
      const errorFromBody =
        typeof data.error === "string"
          ? data.error
          : typeof data.message === "string"
            ? data.message
            : null;

      return {
        success: false,
        error:
          errorFromBody ||
          `Error ${response.status}: ${response.statusText}`,
        code: typeof data.code === "string" ? data.code : undefined,
      };
    }

    const okFlag = data.ok;
    if (okFlag === false) {
      const resultados = data.resultados as
        | Record<string, { status?: string; error?: string } | undefined>
        | undefined;
      const channelErrors = params.canales
        .map((canal) => resultados?.[canal]?.error)
        .filter((msg): msg is string => Boolean(msg));

      return {
        success: false,
        error: channelErrors.join("; ") || "Error al notificar cotización",
      };
    }

    const defaultMessage = params.canales.includes("whatsapp")
      && !params.canales.includes("email")
      ? "WhatsApp enviado"
      : params.canales.includes("email")
        && !params.canales.includes("whatsapp")
        ? "Correo enviado"
        : "Notificación enviada";

    return {
      success: true,
      message:
        typeof data.message === "string" ? data.message : defaultMessage,
      messageId:
        typeof data.messageId === "string" ? data.messageId : undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Error desconocido",
    };
  }
};

/** Email: mismo endpoint, `canales: ["email"]`. */
export const sendQuoteEmailViaBackend = async (
  params: Omit<BuildNotifyQuoteParams, "canales"> & { email: string },
): Promise<SendQuoteNotifyResponse> => {
  return sendQuoteNotifyViaBackend({
    ...params,
    canales: ["email"],
  });
};

/** WhatsApp: mismo endpoint, `canales: ["whatsapp"]`. */
export const sendQuoteWhatsAppViaBackend = async (
  params: Omit<BuildNotifyQuoteParams, "canales"> & { telefono: string },
): Promise<SendQuoteNotifyResponse> => {
  return sendQuoteNotifyViaBackend({
    ...params,
    canales: ["whatsapp"],
  });
};
